import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// One card for now; drop new entries in and the dots/scroll handle the rest.
const CARDS = [
  {
    id: 'recovery',
    base: '/explore-recovery',
    alt: 'Photograph your meal and BodyMax tracks the calories, protein, carbs and fat',
    eyebrow: 'New',
    title: 'Recovery is now part of your plan',
    body: 'Snap a photo of your meal and BodyMax works out the calories, protein, carbs and fat. '
      + 'Log your water and last night\'s sleep in a tap, and see every target set from your own '
      + 'weight, goal and training week.',
    cta: 'See your recovery',
    ctaAway: 'Start your free trial',
  },
];

const scrollToRecovery = () => {
  const el = document.querySelector('[data-testid="recovery-week-card"]');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

/** `brandBlue` locks the accent to BodyMax blue — landing pages never follow the app theme. */
export const ExploreCarousel = ({ ctaTo, brandBlue = false }) => {
  const navigate = useNavigate();
  const accent = brandBlue ? '#0165FE' : 'var(--accent-hex)';
  const accentRgb = brandBlue ? '1,101,254' : 'var(--accent-rgb)';
  const accentText = brandBlue ? '#FFFFFF' : 'hsl(var(--accent-foreground))';
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setIndex(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <div data-testid="explore-carousel">
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory overflow-x-auto scrollbar-hide -mx-1 px-1"
        style={{ scrollbarWidth: 'none' }}
      >
        {CARDS.map((card, i) => (
          <article
            key={card.id}
            className="snap-center shrink-0 w-full px-1"
            data-testid={`explore-card-${card.id}`}
          >
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0B0E13]">
              <img
                src={`${card.base}-800.webp`}
                srcSet={`${card.base}-800.webp 800w, ${card.base}-1200.webp 1200w`}
                sizes="(max-width: 768px) 100vw, 768px"
                width={1254}
                height={1254}
                alt={card.alt}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                className="w-full object-cover"
                style={{ aspectRatio: '1 / 1' }}
                data-testid={`explore-card-image-${card.id}`}
              />
              <div className="px-4 pb-4 pt-3.5">
                <span
                  className="inline-block mb-2 rounded-full px-2.5 py-1 font-heading text-[11px] font-bold uppercase tracking-[0.14em] text-white"
                  style={{ background: `rgba(${accentRgb},0.18)`, border: `1px solid rgba(${accentRgb},0.45)` }}
                  data-testid={`explore-card-eyebrow-${card.id}`}
                >
                  {card.eyebrow}
                </span>
                <p
                  className="font-heading font-black text-white text-lg leading-snug"
                  data-testid={`explore-card-title-${card.id}`}
                >
                  {card.title}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-white/60" data-testid={`explore-card-body-${card.id}`}>
                  {card.body}
                </p>
                <button
                  onClick={() => (ctaTo ? navigate(ctaTo) : scrollToRecovery())}
                  className="mt-4 px-6 py-3 rounded-xl font-heading font-bold text-sm active:scale-[0.97] transition-transform"
                  style={{ background: accent, color: accentText, boxShadow: `0 8px 24px rgba(${accentRgb},0.35)` }}
                  data-testid={`explore-card-cta-${card.id}`}
                >
                  {ctaTo ? (card.ctaAway || card.cta) : card.cta}
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {CARDS.length > 1 && (
        <div className="mt-3 flex justify-center gap-1.5" data-testid="explore-carousel-dots">
          {CARDS.map((card, i) => (
            <span
              key={card.id}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === index ? 18 : 6,
                background: i === index ? accent : 'rgba(255,255,255,0.25)',
              }}
              data-testid={`explore-carousel-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ExploreCarousel;
